import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, type Acceptance } from '../../lib/api'
import { Loading, Alert } from '../../components/ui'
import { voidAcceptance } from '../../lib/voidAcceptance'
import AcceptanceDoc from './AcceptanceDoc'
import { IconButton } from '../../components/IconButton'
import { downloadElementAsPdf, pdfFilename, whenDocReady } from '../../lib/pdfExport'

export default function AcceptancePrint() {
  const { id } = useParams()
  const [a, setA] = useState<Acceptance | null>(null)
  const [error, setError] = useState('')
  const [role, setRole] = useState('')
  const [voiding, setVoiding] = useState(false)
  const docRef = useRef<HTMLDivElement>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [pdfError, setPdfError] = useState('')

  const load = () => {
    api.get<Acceptance>(`/acceptances/${id}`).then(setA).catch((e) => setError(e.message))
  }

  useEffect(load, [id])

  useEffect(() => {
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  // 建完驗收單會直接跳來這一頁，也就是最容易發現建錯的當下，作廢入口放這裡最有用
  const doVoid = async () => {
    if (!a) return
    setError('')
    setVoiding(true)
    try {
      if (await voidAcceptance(a.id, setError)) load()
    } finally {
      setVoiding(false)
    }
  }

  const printWhenReady = async () => {
    await whenDocReady()
    window.print()
  }

  /* 下載 PDF 跟「列印」分開：列印對話框存出來的檔案會受各人的縮放與頁首頁尾設定影響，
     這裡固定參數輸出，寄出去的版本才會一致 */
  const savePdf = async () => {
    if (!docRef.current || !a) return
    setPdfError(''); setPdfBusy(true)
    try {
      await downloadElementAsPdf(docRef.current, pdfFilename('驗收單', a.code, a.writer_name))
    } catch (e: any) {
      setPdfError(`產生 PDF 失敗：${e?.message || e}`)
    } finally {
      setPdfBusy(false)
    }
  }

  if (error) return <Alert kind="error">{error}</Alert>
  if (!a) return <Loading />

  return (
    <>
      <div className="page-head no-print">
        <h2>驗收單列印預覽</h2>
        <div className="spacer" />
        {/* 順序：返回、編輯、下載、列印、作廢 —— 不可逆的作廢放最後，離常用鍵最遠 */}
        <div className="btn-row">
          <IconButton icon="back" to="/acceptances" tip="返回：回到驗收單清單" />
          {!a.voided_at && <IconButton icon="edit" to={`/acceptances/${a.id}/edit`} tip="編輯：修改這張驗收單的內容" />}
          <IconButton icon="download" onClick={savePdf} disabled={pdfBusy}
            tip={pdfBusy ? '正在產生 PDF…' : '下載 PDF：直接存成檔案，版面固定，適合寄給寫手或客戶'} />
          <IconButton icon="print" className="primary" onClick={printWhenReady} disabled={pdfBusy}
            tip="列印：開啟列印對話框，送到印表機" />
          {role === 'admin' && !a.voided_at && (
            <IconButton icon="void" className="danger" onClick={doVoid} disabled={voiding}
              tip={voiding ? '作廢中…' : '作廢：此單將不得作為請款依據，無法復原（僅管理員）'} />
          )}
        </div>
      </div>
      {pdfError && <div className="no-print"><Alert kind="error">{pdfError}</Alert></div>}

      {/* 作廢單不能拿去請款，這條說明放在版面最上方，列印與下載的 PDF 都會帶到 */}
      <AcceptanceDoc
        a={a}
        docRef={docRef}
        banner={a.voided_at && (
          <div
            style={{
              border: '2px solid var(--bad)', color: 'var(--bad)', fontWeight: 700,
              padding: '8px 12px', marginBottom: 12, textAlign: 'center',
            }}
          >
            本驗收單已於 {a.voided_at} 作廢，不得作為請款依據
            {a.void_reason ? `（原因：${a.void_reason}）` : ''}
          </div>
        )}
      />
    </>
  )
}
